import { SLICE_TAG } from '../../../../constants';
import { apiSlice } from '../../api-slice';
import {
    ProductList,
    ProductListResponseV2,
    SignleProductSchema,
} from './product.schema';

export const sessionApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getProducts: builder.query({
            query: (searchParams) => {
                return {
                    url: `products`,
                    method: 'GET',
                    params: searchParams,
                };
            },
            transformResponse: (response) => {
                return ProductList.safeParse(response);
            },
            // onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
            //   try {
            //     const response = await queryFulfilled;
            //     dispatch(setProducts(response.data));
            //   } catch (error) {
            //     console.error('Failed to fetch products:', error);
            //   }
            // },
        }),

        getProduct: builder.query({
            query: (searchParams) => {
                return {
                    url: `products`,
                    method: 'GET',
                    params: searchParams,
                };
            },
            transformResponse: (response) => {
                return SignleProductSchema.safeParse(response);
            },
        }),

        getProductDetailsById: builder.query({
            query: (id) => {
                return {
                    url: `products/${id}`,
                    method: 'GET',
                };
            },
            transformResponse: (response) => {
                return SignleProductSchema.safeParse(response);
            },
        }),

        getProductsWithVariations: builder.query({
            query: (arg) => {
                return {
                    url: `products?variation=true`,
                    method: 'GET',
                    providesTags: ['Products'],
                    keepUnusedDataFor: 600,
                    params: {
                        search: arg.search ?? '',
                        brand_id: arg.brand_id ?? '',
                        generic_id: arg.generic_id ?? '',
                        page: arg.page ?? 1,
                        limit: arg.limit ?? 20,
                        sort_by: arg.sort_by ?? 'name',
                        sort_order: arg.sort_order ?? 'asc',
                    },
                };
            },
        }),

        getProductSigneVariations: builder.query({
            query: (id) => {
                return {
                    url: `products/variations/${id}`,
                    method: 'GET',
                    providesTags: ['Products'],
                    keepUnusedDataFor: 600,
                };
            },
        }),

        createNewProduct: builder.mutation({
            query: (data) => ({
                url: 'products',
                method: 'POST',
                body: data,
                invalidatesTags: [SLICE_TAG.PRODUCTS_V2],
            }),
        }),

        updateProductVariationByVariationId: builder.mutation({
            query: (data) => ({
                url: `products/variation/${data.id}`,
                method: 'POST',
                body: data,
                invalidatesTags: ['Products'],
            }),
        }),

        getProductDetailsByProductVariationId: builder.query({
            query: (id) => {
                return {
                    url: `products/details/${id}`,
                    method: 'GET',
                };
            },
            // transformResponse: (response) => {
            //     return SignleProductSchema.safeParse(response);
            // },
        }),

        // TODO: update the product with batch
        updateProductWithBatch: builder.mutation({
            query: (data) => ({
                url: `products/variation/batch`,
                method: 'PUT',
                body: data,
                invalidatesTags: ['Products'],
            }),
        }),

        updateProductById: builder.mutation({
            query: (data) => ({
                url: `products/${data.id}`,
                method: 'PUT',
                body: {
                    name: data.product_name,
                    brand_id: data.brand_id,
                    generic_id: data.generic_id,
                    shelf: data.shelf,
                },
                invalidatesTags: ['Products'],
            }),
        }),

        exportProductsCsv: builder.query({
            query: (searchParams) => {
                return {
                    url: `products/export/csv`,
                    method: 'GET',
                    responseHandler: (response) => response.blob(),
                    params: searchParams,
                };
            },
            async onQueryStarted(_arg: any, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled; // `data` is a Blob

                    // Extract filename from Content-Disposition if available
                    const disposition = (
                        await fetch('/api/reports/csv', { method: 'HEAD' })
                    ).headers.get('content-disposition');
                    let fileName = `products_${new Date().toISOString()}.csv`;
                    if (disposition && disposition.includes('filename=')) {
                        fileName = disposition
                            .split('filename=')[1]
                            .replace(/"/g, '');
                    }

                    fileName = `products_${new Date().toISOString()}.csv`;

                    // Trigger download
                    const url = window.URL.createObjectURL(data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } catch (err) {
                    console.error('Download failed', err);
                }
                // transformResponse: (response) => {
                //   return SignleProductSchema.safeParse(response);
                // },
            },
        }),

        importProductsCsv: builder.mutation({
            query: (file: File) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: 'products/import/csv',
                    method: 'POST',
                    body: formData,
                    invalidatesTags: ['Products'],
                };
            },
        }),

        getProductsV2: builder.query({
            query: (searchParams) => {
                return {
                    url: `products/v2`,
                    method: 'GET',
                    params: searchParams,
                };
            },

            transformResponse: (response) => {
                return ProductListResponseV2.parse(response);
            },
            providesTags: [SLICE_TAG.PRODUCTS_V2],
        }),

        updateProductDetailsById: builder.mutation({
            query: (payload) => {
                return {
                    url: `products/details/${payload.id}`,
                    method: 'PUT',
                    body: {
                        brand_id: payload.brand_id,
                        generic_id: payload.generic_id,
                        strength_id: payload.strength_id,
                        type_id: payload.type_id,
                        unit_id: payload.unit_id,
                        name: payload.name,
                        description: payload.description,
                        alert_quantity: Number(payload.alert_quantity),
                        purchase_price: Number(payload.purchase_price),
                        wholesale_price: Number(payload.wholesale_price),
                        retail_price: Number(payload.retail_price),
                    },
                    invalidatesTags: [SLICE_TAG.PRODUCTS_V2],
                };
            },
        }),

        getProductNameList: builder.query({
            query: (searchParams) => {
                return {
                    url: `products/name-list`,
                    method: 'GET',
                    params: searchParams,
                };
            },
            // transformResponse: (response) => {
            //     return ProductListResponseV2.parse(response);
            // },
            // providesTags: [SLICE_TAG.PRODUCTS_V2],
        }),
    }),
});

export const {
    useCreateNewProductMutation,
    useGetProductsWithVariationsQuery,
    useGetProductSigneVariationsQuery,
    useGetProductsQuery,
    useLazyGetProductSigneVariationsQuery,
    useLazyGetProductsQuery,
    useLazyGetProductQuery,
    useGetProductQuery,
    useGetProductDetailsByIdQuery,
    useUpdateProductVariationByVariationIdMutation,
    useUpdateProductByIdMutation,
    useUpdateProductWithBatchMutation,
    useLazyGetProductDetailsByIdQuery,
    useLazyExportProductsCsvQuery,
    useImportProductsCsvMutation,

    useGetProductsV2Query,
    useLazyGetProductsV2Query,
    useLazyGetProductsWithVariationsQuery,

    useGetProductDetailsByProductVariationIdQuery,
    useLazyGetProductDetailsByProductVariationIdQuery,

    useExportProductsCsvQuery,

    useUpdateProductDetailsByIdMutation,
    useGetProductNameListQuery,
} = sessionApi;
